import RehabDeviceDetail from "issara-sdk/apis/RehabDeviceDetail_apps_RHBM";
import RehabDeviceList from "issara-sdk/apis/RehabDeviceList_apps_RHB";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  CLEAR: "CLEAR",
  CREATE_DEVICE_FREE: "CREATE_DEVICE_FREE",
  DELETE: "DELETE",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
} as const;

export const SEQUENCE_NAME = "RehabDevice" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  RehabDeviceSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    deviceDetail: RehabDeviceDetailType | null;
    deviceList: RehabDeviceListType[];
    filterDevice: FilterDeviceType;
    showDeviceFree?: boolean;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "selectedDivision">;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type RehabDeviceDetailType = {
  id?: number;
  active: boolean;
  allow_overbooking: boolean;
  device_free?: boolean;
  division?: number;
  end_time: string;
  has_appointments?: boolean;
  inactive_date?: string | null;
  isActive?: boolean;
  name: string;
  qty_of_device?: number;
  start_time: string;
  time_per_slot?: number;
};

type RehabDeviceListType = RehabDeviceDetailType & {
  deleted: boolean;
  device_free: boolean;
  device_free_deleted: boolean;
  has_appointments: boolean;
};

type FilterDeviceType = Partial<{
  division: "" | number;
  include_inactive: boolean;
  include_other_division: boolean;
  search: string;
}>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.DELETE]: { onSuccess: () => void };
  [ACTIONS.SAVE]: { inactiveDate?: string; onSuccess: () => void };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  RehabDeviceSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["division", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const DEFAULT_DEVICE = {
  active: true,
  allow_overbooking: false,
  end_time: "",
  isActive: true,
  name: "",
  start_time: "",
};

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      RehabDeviceSequence: {
        ...state.RehabDeviceSequence,
        sequenceIndex: "Action",
        deviceDetail: { ...DEFAULT_DEVICE },
        filterDevice: { division: state.selectedDivision?.id },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.CLEAR]: HandleClear,
    [ACTIONS.CREATE_DEVICE_FREE]: HandleCreateDeviceFree,
    [ACTIONS.DELETE]: HandleDelete,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */

const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const [items, error] = await GetRehabDeviceList(controller);

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  state = controller.getState();

  const seq = state.RehabDeviceSequence;
  const filter = seq?.filterDevice ?? {};

  let showDeviceFree = seq?.showDeviceFree;

  // เมื่อเลือกแค่ division
  if (Object.keys(filter).length === 1 && filter.division) {
    const hasDeviceFree = items.some((item) => item.device_free);

    showDeviceFree = !hasDeviceFree;
  }

  controller.setState({
    RehabDeviceSequence: { ...seq, deviceList: items, showDeviceFree },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const deviceDetail = state.RehabDeviceSequence?.deviceDetail;

  const api = deviceDetail?.id ? RehabDeviceDetail.update : RehabDeviceList.create;

  const [result, error] = await api({
    apiToken: controller.apiToken,
    data: {
      ...deviceDetail,
      division: state.selectedDivision?.id,
      inactive_date: params.inactiveDate || null,
    },
    pk: deviceDetail?.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  params.onSuccess();

  const showDeviceFree =
    deviceDetail?.device_free && !deviceDetail.id && deviceDetail.active
      ? false
      : state.RehabDeviceSequence?.showDeviceFree;

  controller.setState({
    RehabDeviceSequence: {
      ...state.RehabDeviceSequence,
      deviceDetail: { ...result, isActive: result.active },
      showDeviceFree,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.SUCCESS },
  });

  if (!deviceDetail?.active) {
    HandleClear(controller);
  }

  HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });
};

const HandleDelete: Handler<Params<Actions["DELETE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: BLC_STATUS.LOADING },
  });

  const [, error] = await RehabDeviceDetail.delete({
    apiToken: controller.apiToken,
    pk: state.RehabDeviceSequence?.deviceDetail?.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  params.onSuccess();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: BLC_STATUS.SUCCESS },
  });

  HandleClear(controller);
  HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });
};

const HandleClear: Handler = async (controller) => {
  const state = controller.getState();

  controller.setState({
    RehabDeviceSequence: {
      ...state.RehabDeviceSequence,
      sequenceIndex: "Action",
      deviceDetail: { ...DEFAULT_DEVICE },
    },
  });
};

const HandleCreateDeviceFree: Handler = async (controller) => {
  const state = controller.getState();

  controller.setState({
    RehabDeviceSequence: {
      ...state.RehabDeviceSequence,
      sequenceIndex: "Action",
      deviceDetail: { ...DEFAULT_DEVICE, device_free: true, name: "Device-Free", qty_of_device: 0 },
    },
  });
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const GetRehabDeviceList: Handler<
  FilterDeviceType | undefined,
  Promise<[RehabDeviceListType[], any]>
> = async (controller, params) => {
  const state = controller.getState();

  const filter = params ?? state.RehabDeviceSequence?.filterDevice ?? {};

  const [result, error] = await RehabDeviceList.list({
    apiToken: controller.apiToken,
    params: {
      division: filter.include_other_division ? "" : filter.division,
      include_inactive: filter.include_inactive,
      search: filter.search,
    },
  });

  const items = result?.items || [];

  return [items, error];
};
